import {
  LayoutGrid,
  Rows3,
  Table2,
  Warehouse,
  type LucideIcon,
} from "lucide-react";

import type { ListLayout } from "@/components/list/layout";
import { SELECTED_CHOICE } from "@/components/list/selectedChoice";
import { cn } from "@/lib/utils";

const OPTIONS: Record<ListLayout, { label: string; icon: LucideIcon }> = {
  table: { label: "Table", icon: Table2 },
  grid: { label: "Grid", icon: LayoutGrid },
  tile: { label: "Tiles", icon: Rows3 },
  warehouse: { label: "By warehouse", icon: Warehouse },
};

/**
 * Table, grid or tiles, as icon segments.
 *
 * The same recessed track as `SectionTabs`, at toolbar height, so it reads as
 * a switch among equals rather than as three more buttons beside Filter and
 * Columns. Icons with a label for screen readers and a tooltip: three words in
 * a toolbar already holding a search box would push it to a second line at
 * 1280px.
 */
export function LayoutSwitcher({
  value,
  offered,
  onChange,
}: {
  value: ListLayout;
  offered: readonly ListLayout[];
  onChange: (layout: ListLayout) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Layout"
      className="bg-muted inline-flex items-center gap-0.5 rounded-md p-0.5"
    >
      {offered.map((layout) => {
        const { label, icon: Icon } = OPTIONS[layout];
        const active = layout === value;
        return (
          <button
            key={layout}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            onClick={() => {
              onChange(layout);
            }}
            className={cn(
              "focus-visible:ring-ring rounded p-1.5 transition-colors focus-visible:ring-2 focus-visible:outline-none",
              active
                ? SELECTED_CHOICE
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="size-4" aria-hidden />
          </button>
        );
      })}
    </div>
  );
}
